import React from 'react';
import Icon from 'shared/components/Icon';

function describeContext(context) {
  if (!context || context.type === 'conversation') {
    return {
      icon: 'conversation',
      title: context?.id ? String(context.id) : '',
      ariaLabel: 'Conversation',
      clickable: false,
    };
  }
  if (context.type === 'run') {
    const id = context.id || '';
    return {
      icon: 'timeline',
      title: id,
      ariaLabel: 'Run',
      clickable: true,
    };
  }
  if (context.type === 'span') {
    const label = context.label || context.id || '';
    const started = context.startedAt ? `\nStarted ${context.startedAt}` : '';
    return {
      icon: 'span',
      title: `${label}${context.id ? `\n${context.id}` : ''}${started}`,
      ariaLabel: 'Span',
      clickable: true,
    };
  }
  return {
    icon: 'conversation',
    title: '',
    ariaLabel: 'Conversation',
    clickable: false,
  };
}

/**
 * Icon-only context chip. Run / span chips call onOpenContext so the
 * existing drawers open. Visible label text is omitted on purpose.
 */
export default function AnnotationContextBadge({
  context,
  onOpenContext,
  locked = false,
  className = '',
  /**
   * Name what this comment is on, not just mark it.
   *
   * The icon alone is enough beside a run you are already looking at. In a
   * list that spans a whole conversation — run comments and span comments
   * together — it is not: the reader needs to know that this remark is on
   * the retrieval span and that one is on the run.
   */
  withLabel = false,
}) {
  const { icon, title, ariaLabel, clickable } = describeContext(context);
  const interactive = clickable && !locked && typeof onOpenContext === 'function';
  const shortLabel = context?.type === 'span'
    ? (context.label && context.label !== context.id ? context.label : 'span')
    : context?.type === 'run' ? 'run' : 'conversation';

  const chip = withLabel ? (
    <span
      title={title}
      aria-label={ariaLabel}
      className={`inline-flex max-w-[160px] items-center gap-1 rounded-md border border-[color:var(--border-default)] bg-surface px-1.5 py-0.5 text-[11px] text-[color:var(--text-secondary)] ${className}`}
    >
      <Icon name={icon} size={11} className="flex-none" />
      <span className="truncate">{shortLabel}</span>
    </span>
  ) : (
    <span
      title={title}
      aria-label={ariaLabel}
      className={`inline-flex h-5 w-5 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[color:var(--text-secondary)] ${className}`}
    >
      <Icon name={icon} size={12} className="flex-none" />
    </span>
  );

  if (interactive) {
    return (
      <button
        type="button"
        title={title}
        aria-label={ariaLabel}
        onClick={() => onOpenContext(context)}
        className="inline-flex rounded-md text-left transition-colors hover:text-[color:var(--text-primary)]"
      >
        {chip}
      </button>
    );
  }

  return chip;
}

export { describeContext };
